import { useEffect, useState } from 'react';
import { Popover, TextField } from '@mui/material';
import { ReactComponent as AddIcon } from "assets/images/circled-add.svg";
import { ReactComponent as SearchIcon } from "assets/images/search-icon-2.svg";
import { CurrentBrand } from 'modules/Types';
import { useStyles } from './styles';
import { Body2 } from 'components/shareable/Typography';
import Avatar from '@mui/material/Avatar';
import UpgradeToProChip from 'components/shareable/UpgradeToProChip';
import useOrganization from 'Hooks/useOrganization';
import Colors from 'modules/Colors';
import ButtonSymphony from 'elements/ButtonSymphony';
import { PlanType } from 'modules/Const';
import Sty from 'modules/Sty';

interface AddBrandsPopUpProps {
  brands: CurrentBrand[];
  onAddBrand: (brand: CurrentBrand) => void;
}

const AddBrandsPopUp = ({
  brands,
  onAddBrand
}: AddBrandsPopUpProps) => {
  const classes = useStyles();
  const { getOrganizationBrands } = useOrganization();
  const organizationBrands = getOrganizationBrands();

  const [searchTerm, setSearchTerm] = useState<string>('')
  const filteredOrganizationBrands = organizationBrands.filter((brand) => brand.name.toLowerCase().includes(searchTerm.trim().toLowerCase()));

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Add this check to determine if all brands have been added
  const isAllBrandsAdded = organizationBrands.length > 0 && organizationBrands.every(brand =>
    brands.some(addedBrand => addedBrand.id === brand.id)
  );

  // Close the popover if all brands have been added
  useEffect(() => {
    if (isAllBrandsAdded) {
      setAnchorEl(null);
    }
  }, [isAllBrandsAdded]);

  // Don't render the component if all brands have been added
  if (isAllBrandsAdded) {
    return null;
  }

  /** Renders a single brand item in the add brands popup list */
  const renderBrandToAddItem = (brand: CurrentBrand) => {
    const isAdded = Boolean(brands.find(addedBrand => addedBrand.id === brand.id));

    return (
      <div
        key={`${brand.id}-member-edit-row`}
        className="brandToAddItemRow flex items-center justify-between gap-2"
        style={{ paddingTop: 8, paddingBottom: 8, borderBottom: Sty.border }}
      >
        <div className='brandToAddItemInfo flex gap-4'>
          <Avatar
            className='brandToAddItemAvatar'
            src={brand.image || ''}
            sx={{ width: 24, height: 24 }}
            alt={brand.name}
          />
          <Body2
            className='brandToAddItemName'
            style={{
              textWrap: 'nowrap',
              maxWidth: 100,
              textOverflow: 'ellipsis',
              overflow: 'hidden'
            }}
            color={Colors.textColor}
          >
            {brand.name}
          </Body2>
          {brand.tier === PlanType.PRO &&
            <UpgradeToProChip type='label' />
          }
        </div>
        <ButtonSymphony
          className="addBrandsPopUpButton1"
          variant="text"
          onClick={() => onAddBrand(brand)}
          disabled={isAdded}
          color={isAdded ? 'grey' : 'purple'}
        >
          {isAdded ? 'Added' : 'Add'}
        </ButtonSymphony>
      </div>
    );
  };

  return (
    <>
      {!isAllBrandsAdded &&
        <ButtonSymphony
          className="addBrandsPopUpButton2"
          iconLeft={<AddIcon className="cursor-pointer" height={24} width={24} />}
          onClick={event => setAnchorEl(event.currentTarget)}
          disabled={isAllBrandsAdded}
          variant="text"
        >
          Add a Brand
        </ButtonSymphony>
      }
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ style: { width: 345, maxHeight: 284 } }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className="flex flex-col p-4 gap-4">
          <TextField
            value={searchTerm}
            type="search"
            variant="outlined"
            fullWidth
            className={classes.input}
            placeholder="Search profile"
            onChange={onSearchChange}
            InputProps={{ startAdornment: (<SearchIcon height={24} width={24} className="mr-3" />) }}
          />
          <div className="artistsUpdateBrandsRows flex flex-col max-h-48 overflow-y-scroll">
            {filteredOrganizationBrands.length === 0 ?
              <Body2 color={Colors.grey}>No brands found</Body2>
              :
              filteredOrganizationBrands.map((brand) => renderBrandToAddItem(brand))
            }
          </div>
        </div>
      </Popover>
    </>
  );
};

export default AddBrandsPopUp;